import React, {
	MutableRefObject,
	useRef,
	useState,
	useCallback,
	useEffect
} from 'react';
import './MobileSummaryPage.css';
import { Property, SecondContact } from '../../../../models/Property';
import { TableItem } from '../summary-table/SummaryTable';
import Card from '../../../shared/card/Card';
import Collapsible from '../../../shared/collapsible/collapsible';
import * as Utils from '../../../../utilities/propertyUtilities';
import MapView from '@arcgis/core/views/MapView';
import Map from '@arcgis/core/Map';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { usePDF } from '@react-pdf/renderer';
import PropertyDocument from '../pdf-gen/PropertyDocument';
import BulletList, { checkForBulletPoints } from '../bullet-list/BulletList';
import logo from '../../../../img/Watco_logo_vertical_Color.svg';
import { MobileFooter, MobileHeader } from './mobile-summary-components';

interface MobileSummaryPageProps {
	summaryMapDiv: MutableRefObject<HTMLDivElement | null>;
	map: React.MutableRefObject<__esri.WebMap>;
	extent: __esri.Extent;
	setProperty: (val:Property | undefined) => void;
	property: Property;
}

export default function MobileSummaryPage({ property, setProperty, map, extent }: MobileSummaryPageProps) {
	const { site, building } = property;
	const [currentOpen, setCurrentOpen] = useState(0);

	const document = (<PropertyDocument property={property} />);
	const [instance] = usePDF({ document });

	const internet = Utils.utilityStringBuilder(site?.cabelInternetProvider, site?.cabelInternet);
	const electric = Utils.utilityStringBuilder(site?.electricalProvider, site?.electricalCapcity);
	const gas = Utils.utilityStringBuilder(site?.gasProvider, site?.gasCapacity);
	const stormWater = Utils.utilityStringBuilder(site?.stormWaterProvider, site?.stormWaterCapacity);
	const wasteWater = Utils.utilityStringBuilder(site?.sewerProvider, site?.sewerCapacity);
	const water = Utils.utilityStringBuilder(site?.waterProvider, site?.waterCapacity);

	const amenities = Utils.additionalAmenitiesFormatter(property.additionalAmenities, property.additionalAmenitiesOther);

	const openCollapsible = useCallback((index: number) => {
		setCurrentOpen(index);
	}, [setCurrentOpen]);

	const mapDiv = useRef<HTMLDivElement>(null);

	const view = useRef(new MapView({
		map: new Map({
			basemap: "hybrid"
		}),
		extent: extent,
	}));

	let floodPlain: string;
	if (property.inFloodPlain?.toLowerCase() === 'no') {
		floodPlain = 'No';
	} else if (property.inFloodPlain?.toLowerCase() === 'yes') {
		floodPlain = `Yes - ${property.floodPlainDesignation}`;
	} else {
		floodPlain = 'Unknown';
	}

	const geo = property.geometry as __esri.Polygon;
	const centroid: __esri.Point = geo.centroid;
	const coords: Record<string, string | null> = {
		latitude: centroid.latitude.toFixed(6),
		longitude: centroid.longitude.toFixed(6)
	};

	const coordString = coords.latitude && coords.longitude ? `${coords.latitude} | ${coords.longitude}` : 'N/A';
	const siteTransload = site?.specialtyFeatures?.includes('transload');
	const buildingTransload = building?.specialtyFeatures?.includes('transload');
	const transload = (siteTransload || buildingTransload) ? 'Yes' : 'No';
	//const foreignTradeZone = site?.specialtyFeatures?.includes('foreign_trade_zone') ? 'Yes' : 'No';

	useEffect(() => {
		if (mapDiv.current) {
			view.current.container = mapDiv.current;
		}
	}, []);

	useEffect(() => {
		const source_propertiesLayer = map.current.layers
			.find(layer => layer.title === 'Properties') as __esri.FeatureLayer;

		const propertiesLayer = new FeatureLayer({
			apiKey: source_propertiesLayer.apiKey,
			portalItem: source_propertiesLayer.portalItem,
			renderer: source_propertiesLayer.renderer,
			definitionExpression: `OBJECTID = ${property.OBJECTID}`
		});

		const dupe = view.current.map.layers
			.find(layer =>
				layer.type === 'feature' &&
				(layer as FeatureLayer).portalItem === propertiesLayer.portalItem
			);

		if (dupe) view.current.map.remove(dupe);

		view.current.map.add(propertiesLayer);

		view.current.when(() => {
			view.current.center = property.geometry.extent.center;
			view.current.zoom = 15;
		});

	}, [map, property]);

	const onViewPdf = (pdfLink: string) => {
		const link = pdfLink === 'brochure' ? property.brochureUrl.split(',')[0] : instance.url;

		if (link) {
			window.open(link, "_blank");
		}
	}

	const { otherWatcoContact, brokerContact, brokerEmail, watcoContact, watcoContactOther } = property;
	const secondContact: SecondContact | null = otherWatcoContact
		? { contact: otherWatcoContact, email: 'industrialdevelopment@watco.com', phone: undefined, companyName: null }
		: brokerContact
			? { contact: brokerContact, email: brokerEmail?.toLowerCase(), phone: property.brokerContactPhone, companyName: property.companyName }
			: null;

	const disableBrochureButton = property.brochureUrl ? false : true;
	return (
		<>
			<div className="summary-page--container__mobile">
				<MobileHeader property={property} />
				<div className="summary-page--main-content__mobile">
					<Collapsible
						id={0}
						initiallyOpen={currentOpen === 0 ? true : false}
						onClickCallback={(i: number) => openCollapsible(i)}
						buttonClass='collapsible-button'
						buttonLabel='Description'>
						<div className='p-4'>
							<Card>
								<div className="overview-description">
									{(checkForBulletPoints(property.siteBuildingDescription) && property.siteBuildingDescription)
										? <BulletList description={property.siteBuildingDescription} />
										: <p>{property.siteBuildingDescription}</p>
									}
								</div>
							</Card>
						</div>
					</Collapsible>
					<Collapsible
						id={1}
						initiallyOpen={currentOpen === 1 ? true : false}
						onClickCallback={(i: number) => openCollapsible(i)}
						buttonClass='collapsible-button'
						buttonLabel='Contact Information'>
						<div className='p-4'>
							<Card>
								<div className="contact-us--container">
									<div className="contact-us--content">
										<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap-reverse" }}>
											<div className="contact-us--contact" style={{ marginRight: "auto" }}>
												<div className="contact-us--contact-name">{Utils.checkWatcoContact(watcoContact, watcoContactOther)}</div>
												<div className="contact-us--contact-company">Watco</div>
												<a href={`mailto:${property.watcoContactEmail}`}>{property?.watcoContactEmail?.toLowerCase()}</a>
												{property.watcoContactPhone && <div className="contact-us--contact-phone">
													<a href={`tel: ${property.watcoContactPhone}`}>{property.watcoContactPhone}</a>
												</div>}
											</div>
											<img src={logo} alt="Watco Logo" height={64} style={{ marginRight: "auto" }} />
										</div>
										{secondContact &&
											<div className="contact-us--contact">
												<div className="contact-us--contact-name">{secondContact.contact}</div>
												{secondContact.companyName && <div className="contact-us--contact-company">{secondContact.companyName}</div>}
												{secondContact.email && <a href={`mailto:${secondContact.email}`}>{secondContact.email}</a>}
												{secondContact.phone && <div className="contact-us--contact-phone">
													<a href={`tel: ${secondContact.phone}`}>{secondContact.phone}</a>
												</div>}
											</div>
										}
									</div>
								</div>
							</Card>
						</div>
					</Collapsible>
					<Collapsible
						id={2}
						initiallyOpen={currentOpen === 2 ? true : false}
						onClickCallback={(i: number) => openCollapsible(i)}
						buttonClass='collapsible-button'
						buttonLabel='Site Statistics — Within 50 Miles'>
						<div className='p-4'>
							<Card>
								<div className="metrics--container">
									<div className="metrics--content">
										<div className='metrics-items'>
											<div>
												<div>{property.population ?? '0'}</div>
												<div>Population</div>
											</div>
											<div>
												<div>{property.medianSalary ?? '$0'}</div>
												<div>Median Income</div>
											</div>
											<div>
												<div>{property.workforce ?? '0'}</div>
												<div>Workforce</div>
											</div>
											<div>
												<div>{property.households ?? '--'}</div>
												<div>Number of Households</div>
											</div>
										</div>
									</div>
								</div>
							</Card>
						</div>
					</Collapsible>
					{site &&
						<Collapsible
							id={3}
							onClickCallback={(i: number) => openCollapsible(i)}
							initiallyOpen={currentOpen === 3 ? true : false}
							buttonClass='collapsible-button'
							buttonLabel='Site Profile'
						>
							<div className='p-4'>
								<Card>
									<div className="overview-description">
										<TableItem label={'Acreage'} value={site.areaAcres ?? '0'} />
										<TableItem label={'Within City Limits'} value={site.withinCity ?? 'N/A'} />
										<TableItem label={'County'} value={site.county ?? 'N/A'} />
										<TableItem label={'Latitude/Longitude'} value={coordString ?? 'N/A'} />
										<TableItem label={'Rail Access'} value={site.siteRailServed ?? 'N/A'} />
										<TableItem label={'Outdoor Storage'} value={building?.outdoorStorage ?? 'N/A'} />
										<TableItem label={'Transloading Available'} value={property.transloading} />
										<TableItem label={'Specialty Features'} value={Utils.specialtyFeaturesFormatter(site) ?? 'N/A'} />
										<TableItem label={'Incentives'} value={Utils.incentiveFormatter(property.incentives) ?? 'N/A'} />
										<TableItem label={'Flood Zone'} value={floodPlain} />
										{/*<TableItem label={'Foreign Trade Zone'} value={foreignTradeZone} />*/}
										<TableItem label={'Zoning'} value={Utils.zoningFormatter(site) ?? 'N/A'} />
										<TableItem label={'Additional Amenities'} value={amenities ?? 'N/A'} />
										{!property.building &&
											<TableItem label={'Rail Spots'} value={property.numberOfCarSpots ?? '0'} />
										}
									</div>
								</Card>
							</div>
						</Collapsible>
					}
					{building &&
						<Collapsible
							id={4}
							onClickCallback={(i: number) => openCollapsible(i)}
							initiallyOpen={currentOpen === 4 ? true : false}
							buttonClass='collapsible-button'
							buttonLabel='Building Profile'
						>
							<div className='p-4'>
								<Card>
									<div className="overview-description">
										<TableItem label={'Tenancy'} value={building?.tenancy ?? 'N/A'} />
										<TableItem label={'LTB Ratio'} value={building.ltbRatio ?? '0'} />
										<TableItem label={'Building SqFt'} value={Utils.toFormattedString(building?.areaSqFt) ?? '0'} />
										<TableItem label={'Building Type'} value={building.buildingType ?? 'N/A'} className="capitalize" />
										<TableItem label={'Warehouse SqFt'} value={Utils.toFormattedString(building.warehouseSqFt) ?? '0'} />
										<TableItem label={'Office SqFt'} value={Utils.toFormattedString(building.officeSqFt) ?? '0'} />
										<TableItem label={'Number of Dock Doors'} value={building.numberOfDockDoors ?? '0'} />
										<TableItem label={'Number of Drive-in Doors'} value={building.numberOfDriveInDoors ?? '0'} />
										<TableItem label={'Rail Spots'} value={property.numberOfCarSpots ?? '0'} />
									</div>
								</Card>
							</div>
						</Collapsible>
					}
					<Collapsible
						id={5}
						onClickCallback={(i: number) => openCollapsible(i)}
						initiallyOpen={currentOpen === 5 ? true : false}
						buttonClass='collapsible-button'
						buttonLabel='Utilities'>
						<div className='p-4'>
							<Card>
								<div className="overview-description">
									<TableItem label={'Cable/Internet'} value={internet ?? 'N/A'} />
									<TableItem label={'Electric'} value={electric ?? 'N/A'} />
									<TableItem label={'Natural Gas'} value={gas ?? 'N/A'} />
									<TableItem label={'Stormwater'} value={stormWater ?? 'N/A'} />
									<TableItem label={'Wastewater'} value={wasteWater ?? 'N/A'} />
									<TableItem label={'Water'} value={water ?? 'N/A'} />
								</div>
							</Card>
						</div>
					</Collapsible>
					<Collapsible
						id={6}
						onClickCallback={(i: number) => openCollapsible(i)}
						initiallyOpen={currentOpen === 6 ? true : false}
						buttonClass='collapsible-button'
						buttonLabel='Transportation'>
						<div className='p-4'>
							<Card>
								<div className="overview-description">
									<TableItem label={'Nearest Highway'} value={Utils.propertyHighwayFormatter(property) ?? 'N/A'} />
									<TableItem label={'Nearest Interstate'} value={property.nearestInterstate ?? 'N/A'} />
									<TableItem label={'Nearest Airport'} value={property.nearestAirport ?? 'N/A'} />
									<TableItem label={'Nearest Port'} value={property.nearestPortName ?? 'N/A'} />
									<TableItem label={'Rail-Served'} value={property.site?.siteRailServed ?? property.building?.buildingRailServed ?? 'N/A'} />
									<TableItem label={'Railroad'} value={property.site?.siteRailProvided ?? property.building?.buildingRailProvider ?? 'N/A'} />
								</div>
							</Card>
						</div>
					</Collapsible>
					<Collapsible
						id={7}
						onClickCallback={(i: number) => openCollapsible(i)}
						initiallyOpen={currentOpen === 7 ? true : false}
						buttonClass='collapsible-button'
						buttonLabel='Map'>
						<Card>
							<div className="summary-view-container__mobile">
								<div
									className="summary-map-container__mobile"
									ref={mapDiv}
								></div>
							</div>
						</Card>
					</Collapsible>
				</div>
			</div>
			<MobileFooter
				setProperty={setProperty}
				onViewPdf={onViewPdf}
				propertyDocumentsDisabled={disableBrochureButton}
			/>
		</>
	);
}